import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  GridItem,
  Spinner,
} from '@chakra-ui/react';
import { EmptyState } from 'src/components/molecules/EmptyState';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import { useForecastingDataContext } from '../../../contexts/ForecastingDataContext';
import { CreateForecast } from '../../organisms/CreateForecast';
import { ForecastHeader } from '../../molecules/ForecastTable/ForecastHeader';
import { ForecastRow } from '../../molecules/ForecastTable/ForecastRow';
import { ForecastPanel } from '../../organisms/ForecastPanel';

type ForecastingContentProps = {
  isCreateForecastOpen: boolean;
  onClose: () => void;
};

export const ForecastingContent: React.FC<ForecastingContentProps> = ({
  isCreateForecastOpen,
  onClose,
}) => {
  const { forecasts, forecastComponentsAvailable, isCheckingComponents } =
    useForecastingDataContext();
  const { predictedLabel } = useKpiMetricLabels();

  if (isCheckingComponents || forecasts.isLoading) {
    return (
      <GridItem colSpan={14} w="100%" mt={5}>
        <Center w="100%" h="100%">
          <Spinner speed="0.8s" size="xl" thickness="4px" />
        </Center>
      </GridItem>
    );
  }

  if (!forecastComponentsAvailable) {
    return (
      <GridItem colSpan={14} w="100%" mt={5}>
        <EmptyState
          title="Forecasting not yet available"
          description="Forecasting will be available after your model refresh. The model needs to be re-run to generate the forecast components required for predictions."
        />
      </GridItem>
    );
  }

  if (isCreateForecastOpen) {
    return (
      <GridItem colSpan={14} w="100%" mt={5} overflowX="auto">
        <Box maxW={{ base: '100%', md: '85%', lg: '83%' }}>
          <CreateForecast onClose={onClose} />
        </Box>
      </GridItem>
    );
  }

  if (!forecasts.data || forecasts.data.length === 0) {
    return (
      <GridItem colSpan={14} w="100%" mt={5}>
        <EmptyState
          title="No forecasts yet"
          description={`Create your first forecast to see ${predictedLabel.toLowerCase()} based on planned media spend.`}
        />
      </GridItem>
    );
  }

  return (
    <GridItem colSpan={14} w="100%" mt={5} overflowX="auto">
      <Box minW="fit-content">
        <ForecastHeader />
        <Accordion allowMultiple>
          {forecasts.data.map((forecast) => (
            <AccordionItem key={forecast.forecastId} minW="fit-content">
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton p={0} minW="fit-content">
                      <ForecastRow
                        forecast={forecast}
                        isExpanded={isExpanded}
                      />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <ForecastPanel forecast={forecast} isOpen={isExpanded} />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    </GridItem>
  );
};
